import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, Lock } from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

/**
 * Read-only master data viewer.
 * These tables (programs / entities / specializations / categories) are
 * owned by the campus platform, not this app — no upload/edit here.
 *
 * Props:
 *  - kind: 'programs' | 'entities' | 'specializations' | 'categories'
 *  - title, subtitle
 *  - columns: array of { key, label, mono? }
 */
export default function MasterUploader({ kind, title, subtitle, columns }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [q, setQ] = useState("");

  const loadItems = async () => {
    try {
      const r = await api.get(`/master/${kind}`, { params: { q, page, page_size: pageSize } });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  useEffect(() => { loadItems(); }, [q, page, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid={`master-${kind}-page`}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <Badge variant="outline" className="flex items-center gap-1.5">
            <Lock size={12} /> Read-only
          </Badge>
        }
      />

      <div className="bg-white border border-border rounded-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border gap-3">
          <div className="relative w-full md:w-96">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={`search-${kind}`}
              className="pl-8"
              placeholder="Search…"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
            />
          </div>
          <p className="text-sm text-slate-500 shrink-0">
            {total} record{total === 1 ? "" : "s"} · Page {page} / {pages}
          </p>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={columns.length} className="text-center py-10 text-slate-500">
                  No records found.
                </TableCell></TableRow>
              )}
              {items.map((it, i) => (
                <TableRow key={i} data-testid={`row-${kind}-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.mono ? "font-mono text-xs" : ""}>
                      {String(it[c.key] ?? "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-border">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid={`prev-${kind}`}>
            <ChevronLeft size={14} /> Prev
          </Button>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid={`next-${kind}`}>
            Next <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
